import { z } from "zod";
import {
  ActionKind,
  ActionStatus,
  FindingCategory,
  ParticipantStatus,
  SectorStatus,
  UnitType,
} from "./enums";
import { FindingIcon } from "./finding-icons";
import { MultiLineStringGeometry, MultiPolygonGeometry, PointGeometry } from "./geo";

// A read-only view of an action for other services (Police, fire brigade, army) through a
// temporary link (issue #163). The token is only in the URL fragment, the database keeps its
// hash. The view has no chat, no subject card, no phone numbers and no medical notes.

const Id = z.uuid();
const Timestamp = z.iso.datetime({ offset: true });

/** Longest validity of a link: one day of an exercise or an operation. */
export const SHARE_LINK_MAX_HOURS = 24;
/** Older positions are not shown: someone not heard from for this long is not on the ground. */
export const SHARE_POSITION_MAX_HOURS = 6;

export const CreateShareLinkInput = z.object({
  /** Who it is for, e.g. "Policja – dyżurny"; shown to HQ and on the shared page. */
  label: z.string().trim().min(2).max(80),
  validHours: z.number().int().min(1).max(SHARE_LINK_MAX_HOURS),
});
export type CreateShareLinkInput = z.infer<typeof CreateShareLinkInput>;

export const ShareLinkDto = z.object({
  id: Id,
  actionId: Id,
  label: z.string(),
  createdByName: z.string(),
  createdAt: Timestamp,
  expiresAt: Timestamp,
  revokedAt: Timestamp.nullable(),
  /** Last time someone opened the view (updated at most once a minute). */
  lastUsedAt: Timestamp.nullable(),
  /** HQ let this link watch the live cameras (#349), off by default. */
  canViewVideo: z.boolean(),
});
export type ShareLinkDto = z.infer<typeof ShareLinkDto>;

/** HQ gives or takes the cameras at any time after the link was made (#349). */
export const UpdateShareLinkInput = z.object({ canViewVideo: z.boolean() });
export type UpdateShareLinkInput = z.infer<typeof UpdateShareLinkInput>;

export const SharedSector = z.object({
  id: Id,
  name: z.string(),
  status: SectorStatus,
  areaHa: z.number(),
  /** Coverage by track (estimated), percent. */
  coveredPct: z.number(),
  geometry: MultiPolygonGeometry,
  /** Simplified covered area, null before the first computation. */
  covered: MultiPolygonGeometry.nullable(),
  groupName: z.string().nullable(),
});
export type SharedSector = z.infer<typeof SharedSector>;

export const SharedParticipant = z.object({
  id: Id,
  name: z.string(),
  status: ParticipantStatus,
  unitType: UnitType,
  groupName: z.string().nullable(),
  location: PointGeometry,
  /** When the position was recorded. */
  at: Timestamp,
});
export type SharedParticipant = z.infer<typeof SharedParticipant>;

export const SharedFinding = z.object({
  id: Id,
  category: FindingCategory,
  icon: FindingIcon.nullable(),
  description: z.string().nullable(),
  location: PointGeometry,
  foundAt: Timestamp,
  /** A photo whose file is stored: GET /api/share/photos/:id. */
  photoId: Id.nullable(),
});
export type SharedFinding = z.infer<typeof SharedFinding>;

/** Where a drone flew (#349): every segment of its track, drawn as one simplified line. */
export const SharedDroneTrack = z.object({
  participantId: Id,
  geometry: MultiLineStringGeometry,
});
export type SharedDroneTrack = z.infer<typeof SharedDroneTrack>;

export const SharedView = z.object({
  action: z.object({ name: z.string(), status: ActionStatus, kind: ActionKind }),
  /** Who the link is for (e.g. "Policja – dyżurny"): shown on the page, so they know it is theirs. */
  label: z.string(),
  expiresAt: Timestamp,
  sectors: z.array(SharedSector),
  participants: z.array(SharedParticipant),
  findings: z.array(SharedFinding),
  droneTracks: z.array(SharedDroneTrack),
  /** The link may watch the cameras now (GET /api/share/video); HQ can switch it any time. */
  canViewVideo: z.boolean(),
});
export type SharedView = z.infer<typeof SharedView>;

/** The link HQ hands over; the token stays in the fragment, never sent to a server. */
export function buildShareLink(publicUrl: string, token: string): string {
  return `${publicUrl.replace(/\/$/, "")}/podglad#${token}`;
}
